import { useState, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Download, Trash2, Copy as CopyIcon, Move } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';
import { cn } from '@/lib/cn';

interface PageThumb {
  index: number; // original index
  url: string;
}

export function PdfThumbnails() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [thumbs, setThumbs] = useState<PageThumb[]>([]);
  const [order, setOrder] = useState<number[]>([]); // pointers to original indices
  const [working, setWorking] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(f);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const list: PageThumb[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale: 0.4 });
        const c = document.createElement('canvas');
        c.width = viewport.width;
        c.height = viewport.height;
        const ctx = c.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
        list.push({ index: i - 1, url: c.toDataURL('image/png') });
      }
      setThumbs(list);
      setOrder(list.map((t) => t.index));
    } finally {
      setWorking(false);
    }
  }

  function move(from: number, to: number) {
    if (from === to) return;
    setOrder((arr) => {
      const next = arr.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }

  function duplicate(visualIdx: number) {
    setOrder((arr) => {
      const next = arr.slice();
      next.splice(visualIdx + 1, 0, next[visualIdx]);
      return next;
    });
  }

  function remove(visualIdx: number) {
    setOrder((arr) => arr.filter((_, i) => i !== visualIdx));
  }

  async function exportPdf() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const src = await PDFDocument.load(buf);
      const out = await PDFDocument.create();
      const copied = await out.copyPages(src, order);
      copied.forEach((p) => out.addPage(p));
      const bytes = await out.save();
      saveAs(bytesToBlob(bytes), file.name.replace(/\.pdf$/i, '') + '-organized.pdf');
      toast.success('Exported');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-thumbnails"
        title="Visual page reorder"
        description="Visual thumbnails of every page. Drag to reorder, click to duplicate or delete, then export the new PDF."
        badges={file ? <Badge>{order.length} pages</Badge> : null}
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <>
          <Card className="mb-4 flex items-center justify-between">
            <div className="text-sm font-medium truncate">{file.name}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setThumbs([]); setOrder([]); }}>
                Change
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={working}
                disabled={!order.length}
                onClick={exportPdf}
              >
                <Download size={11} />
                Export
              </Button>
            </div>
          </Card>

          {working && !thumbs.length ? (
            <Card><div className="py-8 text-center text-sm text-fg-subtle">Rendering thumbnails…</div></Card>
          ) : (
            <Card className="!p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {order.map((origIdx, visualIdx) => {
                  const thumb = thumbs.find((t) => t.index === origIdx);
                  if (!thumb) return null;
                  return (
                    <div
                      key={`${origIdx}-${visualIdx}`}
                      draggable
                      onDragStart={() => setDragging(visualIdx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragging !== null) move(dragging, visualIdx);
                        setDragging(null);
                      }}
                      className={cn(
                        'group relative rounded-lg border-2 transition cursor-grab active:cursor-grabbing',
                        dragging === visualIdx ? 'opacity-40 border-accent' : 'border-border hover:border-accent/40',
                      )}
                    >
                      <img
                        src={thumb.url}
                        alt={`page ${origIdx + 1}`}
                        className="w-full h-auto rounded block bg-bg-elev"
                      />
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-2xs font-mono rounded bg-bg/80 backdrop-blur border border-border">
                        {visualIdx + 1}
                      </div>
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => duplicate(visualIdx)}
                          className="size-6 rounded bg-bg/80 backdrop-blur border border-border hover:bg-surface-hover flex items-center justify-center text-fg-muted hover:text-fg"
                          title="Duplicate"
                        >
                          <CopyIcon size={10} />
                        </button>
                        <button
                          onClick={() => remove(visualIdx)}
                          className="size-6 rounded bg-bg/80 backdrop-blur border border-border hover:bg-danger/10 flex items-center justify-center text-fg-muted hover:text-danger"
                          title="Remove"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                      <div className="absolute inset-x-0 bottom-1 flex justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none">
                        <Move size={11} className="text-fg-subtle" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default PdfThumbnails;
